import { eff } from "@/core/utils/effect-ts-abbr";
import { RangeFilterItemDto } from "@/shared/api-contract/api-contract";
import { AppRangeSliderModel } from "@/shared/components/app-range-slider/app-range-slider-model";
import { AppChartOptions } from "@/shared/directives/app-chart";
import { InputHTMLAttributes } from "@vue/runtime-dom";
import { debounce } from "throttle-debounce-ts";
import { watchEffect } from "vue";
import { Emit, Model, Prop, Vue } from "vue-property-decorator";
import "./range-slider.scss";
import { isNil } from "lodash";
import { theme, themes } from "@/themes";

export class RangeSliderComponent extends Vue {
    @Prop()
    placeholder?: string;

    @Prop()
    unit?: string;

    /**
     * Assumed that items are already sorted.
     */
    @Prop({ required: true, type: Array })
    options!: RangeFilterItemDto[];

    @Model("modelValue")
    model!: AppRangeSliderModel;

    @Prop({ default: null })
    minPossibleValue!: number;

    @Prop({ default: null })
    maxPossibleValue!: number;

    @Prop({
        default: (it: string) => {
            const value = parseFloat(it);

            return isNaN(value) ? null : value;
        },
        type: Function,
    })
    fromString!: (value: string) => number;

    @Prop({ default: eff.ord.number, type: Object })
    ord!: eff.ord.Ord<number>;

    @Prop({ type: Object })
    minAttrs!: InputHTMLAttributes;

    @Prop({ type: Object })
    maxAttrs!: InputHTMLAttributes;

    /**
     * Block dropdown when no options provided.
     */
    @Prop({ default: true, type: Boolean })
    blockEmpty!: boolean;

    /**
     * Block dropdown when single item provided in the options.
     */
    @Prop({ default: false, type: Boolean })
    blockSingle!: boolean;

    changeMinValue!: (value: number, fromInput?: boolean) => void;
    changeMaxValue!: (value: number, fromInput?: boolean) => void;

    get blocked() {
        return (
            (!this.options.length && this.blockEmpty) ||
            (this.options.length <= 1 && this.blockSingle)
        );
    }

    get from(): number {
        return isNil(this.model.from) || this.model.from === -Infinity
            ? this.options[0]?.min_value || 0
            : this.options[this.model.from]?.min_value || 0;
    }

    get to(): number {
        return isNil(this.model.to) || this.model.to === -Infinity
            ? this.options[this.options.length - 1]?.max_value
            : this.options[this.model.to]?.max_value;
    }

    get maxOptionValue(): number {
        return eff.fn.pipe(
            this.options,
            eff.array.last,
            eff.option.fold(
                () => this.maxPossibleValue,
                (option) => option.max_value,
            ),
        );
    }

    get minOptionValue(): number {
        return eff.fn.pipe(
            this.options,
            eff.array.head,
            eff.option.fold(
                () => this.minPossibleValue,
                (option) => option.min_value,
            ),
        );
    }

    get sliderOptions() {
        const fromValue = this.model.from;
        const toValue = this.model.to;

        const startMin = isNil(fromValue) || fromValue === -Infinity ? 0 : fromValue;

        const startMax =
            isNil(toValue) || toValue === -Infinity ? this.options.length - 1 : toValue;

        return {
            start: [startMin, Math.max(startMax, startMin + 1)],
            step: 1,
            connect: true,
            range: {
                min: 0,
                max: Math.max(this.options.length - 1, 1),
            },
        };
    }

    get chartOptions(): AppChartOptions<"bar"> {
        return {
            initialConfig: {
                type: "bar",
            },
            data: {
                labels: eff.fn.pipe(
                    this.options,
                    eff.array.map((item) => item.name),
                ) as string[],
                datasets: [
                    {
                        data: eff.fn.pipe(
                            this.options,
                            eff.array.map((item) => item.counter),
                        ) as number[],
                        backgroundColor: [
                            theme?.colors?.["color-chart-bar"] ??
                                themes?.carconnex?.colors?.["color-chart-bar"],
                        ],
                    },
                ],
            },
            options: {
                maintainAspectRatio: false,
                responsive: false,
                plugins: {
                    legend: {
                        display: false,
                    },
                    tooltip: {
                        intersect: false,
                    },
                },
                scales: {
                    xAxes: {
                        display: false,
                    },
                    yAxes: {
                        display: false,
                    },
                },
                animation: {
                    duration: 0,
                },
            },
        };
    }

    @Emit("blocked")
    emitBlocked() {
        return this.blocked;
    }

    mounted() {
        watchEffect(() => {
            this.emitBlocked();
        });
    }

    created() {
        this.changeMinValue = debounce(200, (value: number, fromInput?: boolean) => {
            console.log(value);
            // debugger;
            this.model = {
                ...this.model,
                from: fromInput ? this.getModelPosition(value) : value,
            };
        });

        this.changeMaxValue = debounce(200, (value: number, fromInput?: boolean) => {
            this.model = {
                ...this.model,
                to: fromInput ? this.getModelPosition(value) : value,
            };
        });
    }

    getModelPosition(value: number) {
        let pos = 0;

        console.log(this.options);

        (this.options || []).forEach((item, index) => {
            if (item.min_value <= value && item.max_value >= value) {
                pos = index;
            }
        });

        return pos;
    }

    onSliderChange(event: CustomEvent<readonly string[]>) {
        if (!this.options.length) {
            return;
        }

        const [min, max] = event.detail;

        const minIndex = parseInt(min, 10);
        const maxIndex = parseInt(max, 10);

        console.log(maxIndex);

        this.changeMinValue(minIndex);
        this.changeMaxValue(maxIndex);
    }

    onChangeMinValue(event: Event) {
        const target = event.target as HTMLInputElement;

        this.changeMinValue(this.fromString(target.value), true);
    }

    onChangeMaxValue(event: Event) {
        const target = event.target as HTMLInputElement;

        this.changeMaxValue(this.fromString(target.value), true);
    }
}
